import { combineQueries, QueryEntity } from '@datorama/akita';
import { DateTimeService } from '@y42/date-time';
import { GitQuery } from '@y42/git';
import { ImpexChildQuery } from '@y42/impex';
import { JobStatus, Y42FeatureType } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import { filter, map, Observable, shareReplay } from 'rxjs';
import { DatasourceTableJobUIModel } from '../datasource-table-job/datasource-table-job.model';
import { DatasourceTableJobQuery } from '../datasource-table-job/datasource-table-job.query';
import { DatasourceState } from '../datasource/datasource.model';
import { DatasourceTableState, DatasourceTableStoreModel, DatasourceTableUIModel } from './datasource-table.model';
import { DatasourceTableStore } from './datasource-table.store';

export abstract class DatasourceTableQuery extends ImpexChildQuery<DatasourceTableState, DatasourceTableUIModel> {
  constructor(
    protected readonly store: DatasourceTableStore,
    protected readonly parentQuery: QueryEntity<DatasourceState>,
    protected readonly gitQuery: GitQuery,
    protected readonly childQuery: DatasourceTableJobQuery,
    protected readonly dateTimeService: DateTimeService,
  ) {
    super({
      parentQuery,
      store,
      toUIModel: (storeModel) => {
        storeModel = new DatasourceTableStoreModel(storeModel);

        const jobs = (storeModel?.$children || [])
          .map((jobId) => this.childQuery.getUIEntity(jobId))
          .filter(isNotNullish)
          // sort newest jobs first
          .sort((a, b) => {
            if (!b.created_at && !a.created_at) {
              return 0;
            }

            if (!b.created_at || !a.created_at) {
              return (b.created_at ? 1 : 0) - (a.created_at ? 1 : 0);
            }

            return new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf();
          });

        const latest_job: DatasourceTableJobUIModel | undefined = jobs[0];
        const latest_valid_job =
          latest_job?.status === JobStatus.Ready ? latest_job : jobs.find(({ status }) => status === JobStatus.Ready);
        const latest_valid_chain_parent = latest_valid_job
          ? jobs.find((job) => latest_valid_job.parent_job_id === job.request_id)
          : undefined;

        return {
          featureType: this.featureType,
          ...storeModel,
          datasource: this.parentQuery.getEntity(storeModel.$parent)!,
          jobs,
          latest_job: latest_job ? this.childQuery.toUIModel(latest_job) : undefined,
          latest_valid_job: latest_valid_job ? this.childQuery.toUIModel(latest_valid_job) : undefined,
          latest_valid_chain_parent,
        };
      },
    });
  }

  readonly tables$: Observable<DatasourceTableUIModel[]> = combineQueries([
    this.selectAll(),
    this.childQuery.selectAll(),
  ]).pipe(
    map(([tables]) => tables.map(this.toUIModel)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly active$: Observable<DatasourceTableUIModel> = combineQueries([
    this.selectActive().pipe(filter(Boolean)),
    this.childQuery.selectAll(),
  ]).pipe(map(([active]) => this.toUIModel(active)));

  readonly activeItems$: Observable<DatasourceTableUIModel[]> = combineQueries([
    this.parentQuery.selectActive(),
    this.childQuery.selectAll(),
  ]).pipe(
    map(([activeParent]) =>
      activeParent ? activeParent.$children.map((child) => this.getUIEntity(child)).filter(isNotNullish) : [],
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  abstract readonly featureType: Y42FeatureType;
}
